import React, { useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  usePatientAllergies,
  usePatientMedicalProfile,
  getAllergyDisplayState,
} from '../../hooks/queries/usePatientAllergies';
import { buildCompositePatientId } from '../../utils/patientUtils';
import { dedupeCi } from '../../utils/allergyUtils';
import MedicalProfileDialog from './MedicalProfileDialog';
import { tintWarningCard, tintSuccessCard, textSuccess, textWarningMed } from '../../theme/gridSurfaces';

export interface PatientAllergiesCardProps {
  pharmacyId: string | undefined;
  patientId: string | undefined;
  /** Optional sx overrides applied to the outer Paper. */
  sx?: SxProps<Theme>;
  /** Reduce vertical padding for tighter banner-style placement. */
  dense?: boolean;
  /**
   * Show an edit affordance that opens the shared MedicalProfileDialog, so
   * PHOX allergies/conditions can be amended without leaving the workflow
   * (Counseling, Clinical Verification — GP-1151).
   */
  editable?: boolean;
  /**
   * Also render a Conditions row: PHOX-registered medical conditions (from the
   * same medical-profile query) merged with the optional `conditionsText`
   * (GP-1151 follow-up: conditions added on Patient 360 were invisible in
   * Counseling / Clinical Verification).
   */
  showConditions?: boolean;
  /** Kroll/order-sourced conditions, comma-separated (e.g. ICD descriptions). */
  conditionsText?: string;
  /**
   * Also render the patient's PHOX-registered Weight (GP-1227). Surfaced inside
   * Counseling so the pharmacist can read/record weight without leaving the
   * worksheet — editing happens in the same MedicalProfileDialog the edit
   * affordance opens.
   */
  showWeight?: boolean;
  /**
   * Also render a count of PHOX-registered medications (BPMH) (GP-1227). Keeps
   * the counseling rail's edit affordance discoverable for the BPMH list; the
   * full list is edited in the shared dialog.
   */
  showMedications?: boolean;
}

/**
 * Three mutually exclusive allergy states sourced from Kroll, surfaced uniformly
 * across Counseling, Clinical Verification, and Product Verification:
 *
 *   has-allergies  → list documented allergies
 *   nka-confirmed  → "No Known Allergies" confirmed in Kroll (green)
 *   not-documented → WARNING: no allergy info entered, verify with patient (orange)
 *
 * The "not-documented" warning is the key difference from a naive listing — it
 * prevents pharmacists from mistaking a blank field for "no allergies".
 */
const PatientAllergiesCard: React.FC<PatientAllergiesCardProps> = ({
  pharmacyId,
  patientId,
  sx,
  dense = false,
  editable = false,
  showConditions = false,
  conditionsText,
  showWeight = false,
  showMedications = false,
}) => {
  const { data: allergyStatus, isLoading } = usePatientAllergies(pharmacyId, patientId);
  // PHOX-owned allergies (GP-1135) — registered on the Patient 360 file and
  // merged in here so the master list is consistent everywhere it's shown.
  const compositeId = buildCompositePatientId(patientId, pharmacyId);
  const { data: medicalProfile } = usePatientMedicalProfile(compositeId || undefined);
  const displayState = getAllergyDisplayState(allergyStatus);
  const [dialogOpen, setDialogOpen] = useState(false);

  const padding = dense ? 1 : 1.5;

  // Kroll allergy descriptions (blank/sentinel rows dropped) + PHOX-registered
  // allergies, combined into one de-duplicated master list.
  const krollDescriptions = (allergyStatus?.allergies || [])
    .map((a) => a.description?.trim())
    .filter((d): d is string => !!d && d.length > 0);
  const phoxAllergies = medicalProfile?.allergies || [];
  const combined = dedupeCi([...phoxAllergies, ...krollDescriptions]);

  // Effective state: any documented allergy (PHOX or Kroll) wins for safety —
  // never show a green "No Known Allergies" when PHOX has a registered allergy.
  const effectiveState =
    combined.length > 0 ? 'has-allergies' : displayState;

  const valueNode = (() => {
    if (isLoading && !allergyStatus && !medicalProfile) {
      return (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      );
    }
    if (combined.length > 0) {
      return <Typography variant="body2">{combined.join(', ')}</Typography>;
    }
    // No described allergies from either source. If Kroll had rows that all
    // normalized blank, warn rather than render nothing.
    if (displayState === 'has-allergies') {
      return (
        <Typography variant="body2" sx={{ color: textWarningMed, fontWeight: 500 }}>
          ⚠ {allergyStatus!.allergies.length} allergy row(s) found in Kroll but with no
          description — verify with patient before dispensing
        </Typography>
      );
    }
    if (displayState === 'nka-confirmed') {
      return (
        <Typography variant="body2" sx={{ color: textSuccess, fontWeight: 500 }}>
          No Known Allergies (confirmed in Kroll)
        </Typography>
      );
    }
    return (
      <Typography variant="body2" sx={{ color: textWarningMed, fontWeight: 500 }}>
        ⚠ No allergy information in Kroll — verify with patient before dispensing
      </Typography>
    );
  })();

  // Conditions row (opt-in): PHOX-registered conditions first (they're the
  // staff-curated list), then any Kroll/order-sourced text, deduped. The
  // medical-profile query is invalidated on save, so a condition added via the
  // dialog appears here immediately.
  const combinedConditions = showConditions
    ? dedupeCi([
        ...(medicalProfile?.medicalConditions || []),
        ...(conditionsText ? conditionsText.split(',').map((s) => s.trim()) : []),
      ])
    : [];
  const conditionsNode = showConditions ? (
    <Typography variant="body2">
      {combinedConditions.length > 0 ? combinedConditions.join(', ') : 'None documented'}
    </Typography>
  ) : null;

  // Weight & BPMH meds (GP-1227): opt-in rows so Counseling surfaces the full
  // medical profile the edit dialog manages, not just allergies/conditions.
  const weightText = medicalProfile?.weight?.trim() || '';
  const weightNode = showWeight ? (
    <Typography variant="body2">
      {weightText || 'Not recorded'}
    </Typography>
  ) : null;
  const medsCount = medicalProfile?.medications?.length || 0;
  const medicationsNode = showMedications ? (
    <Typography variant="body2">
      {medsCount > 0 ? `${medsCount} on file` : 'None registered'}
    </Typography>
  ) : null;

  // Edit affordance (opt-in): opens the shared editor for the PHOX-owned
  // profile. Needs the composite id — without it there is nothing to edit.
  const editNode =
    editable && compositeId ? (
      <Tooltip title="Add / manage PHOX allergies & medical conditions">
        <IconButton
          size="small"
          aria-label="Manage allergies and medical conditions"
          onClick={() => setDialogOpen(true)}
          sx={{ ml: 'auto', flexShrink: 0, my: -0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: padding,
        ...(effectiveState === 'not-documented' && {
          bgcolor: tintWarningCard,
          borderColor: textWarningMed,
        }),
        ...(effectiveState === 'nka-confirmed' && {
          bgcolor: tintSuccessCard,
          borderColor: textSuccess,
        }),
        ...sx,
      }}
    >
      {dense ? (
        // Banner placement: label + status on one line to save vertical space
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
              Allergies:
            </Typography>
            {valueNode}
            {editNode}
          </Box>
          {conditionsNode && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
                Conditions:
              </Typography>
              {conditionsNode}
            </Box>
          )}
          {weightNode && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
                Weight:
              </Typography>
              {weightNode}
            </Box>
          )}
          {medicationsNode && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
                Meds (BPMH):
              </Typography>
              {medicationsNode}
            </Box>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Allergies
            </Typography>
            {editNode}
          </Box>
          {valueNode}
          {conditionsNode && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Conditions
              </Typography>
              {conditionsNode}
            </>
          )}
          {weightNode && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Weight
              </Typography>
              {weightNode}
            </>
          )}
          {medicationsNode && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Meds (BPMH)
              </Typography>
              {medicationsNode}
            </>
          )}
        </>
      )}

      {/* Mount-gated so each open starts a fresh editing session. */}
      {dialogOpen && compositeId && (
        <MedicalProfileDialog open onClose={() => setDialogOpen(false)} patientId={compositeId} />
      )}
    </Paper>
  );
};

export default PatientAllergiesCard;
